import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { romaneioService, Romaneio } from '../services/romaneio';

const ConferenciaRomaneioSaida: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRomaneio, setSelectedRomaneio] = useState<Romaneio | null>(null);

  const { data: romaneios = [], isLoading, error } = useQuery({
    queryKey: ['romaneios'],
    queryFn: romaneioService.getAll,
  });

  const filteredRomaneios = useMemo(() => {
    if (!search.trim()) return romaneios;
    const searchLower = search.toLowerCase();
    return romaneios.filter((romaneio) => {
      if (romaneio.id?.toString().includes(searchLower)) return true;
      if (romaneio.nomePromotor?.toLowerCase().includes(searchLower)) return true;
      if (romaneio.nomePraca?.toLowerCase().includes(searchLower)) return true;
      if (romaneio.dataSaidaString?.toLowerCase().includes(searchLower)) return true;
      if (searchLower === 'devolvido' && romaneio.devolvido) return true;
      if (searchLower === 'não devolvido' && !romaneio.devolvido) return true;
      return false;
    });
  }, [romaneios, search]);

  const paginatedRomaneios = useMemo(() => {
    return filteredRomaneios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRomaneios, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, romaneio: Romaneio) => {
    setAnchorEl(event.currentTarget);
    setSelectedRomaneio(romaneio);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRomaneio(null);
  };

  const handleEditar = (id: string) => {
    navigate(`/ConferenciaRomaneioSaida/Editar/${id}`);
    handleMenuClose();
  };

  const handleVoltar = () => {
    navigate('/romaneio');
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography color="error">Erro ao carregar os romaneios.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Romaneio Conferência Saída
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleVoltar}>
          Voltar
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            placeholder="Filtrar"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Promotor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data de Saída</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRomaneios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography sx={{ py: 4, color: 'text.secondary' }}>
                      {search ? 'Nenhum romaneio encontrado para o filtro informado.' : 'Nenhum romaneio cadastrado.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRomaneios.map((romaneio) => (
                  <TableRow key={romaneio.id} hover>
                    <TableCell>{romaneio.id}</TableCell>
                    <TableCell>{romaneio.nomePromotor}</TableCell>
                    <TableCell>{romaneio.nomePraca}</TableCell>
                    <TableCell>{romaneio.dataSaidaString}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => handleMenuClick(e, romaneio)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedRomaneio?.id === romaneio.id}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={() => handleEditar(romaneio.id)}>
                          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                          Editar
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRomaneios.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} itens`}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': { pl: 2, pr: 2 }
          }}
        />
      </Paper>
    </Container>
  );
};

export default ConferenciaRomaneioSaida;
